import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import { CreditCard, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";

export default function PricingPage() {
  const [pkgs, setPkgs] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packages");
        setPkgs(data);
      } catch (error) {
        logger.error("Load packages failed", error);
      }
    })();
  }, []);

  const buy = async (id) => {
    setErr("");
    setLoadingId(id);
    try {
      const { data } = await api.post("/checkout", { package_id: id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      logger.error("Checkout failed", e);
      setErr(formatError(e.response?.data?.detail));
      toast.error("Checkout failed");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="pricing-page">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white mb-2 font-heading">Buy Credits</h1>
          <p className="text-zinc-500">Start for just $2. The more you buy, the more you save.</p>
        </div>
        {err && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 mb-6 text-center" data-testid="pricing-error">{err}</div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {pkgs.map((p) => {
            const isPop = p.badge === "popular";
            const isBest = p.badge === "best_value";
            return (
              <div key={p.id} className={`border p-6 bg-white dark:bg-zinc-900 transition-all hover:-translate-y-1 relative ${isPop ? "border-brand ring-2 ring-brand shadow-lg shadow-blue-500/5" : isBest ? "border-green-500 ring-2 ring-green-500" : "border-zinc-200 dark:border-zinc-700 hover:shadow-lg"}`} data-testid={`package-card-${p.id}`}>
                {isPop && <div className="absolute -top-3 left-4 bg-brand text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Popular</div>}
                {isBest && <div className="absolute -top-3 left-4 bg-green-500 text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Best Value</div>}
                <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-1 font-heading">{p.name}</h3>
                <div className="text-3xl font-black text-zinc-950 dark:text-white tracking-tighter mb-0.5 font-heading">${p.amount.toFixed(0)}</div>
                <p className="text-zinc-500 text-xs mb-1">{p.credits} credits</p>
                <p className="text-zinc-400 text-xs mb-4">${(p.amount / p.credits).toFixed(2)}/credit</p>
                <button data-testid={`buy-${p.id}-btn`} onClick={() => buy(p.id)} disabled={loadingId === p.id} className={`w-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${isPop ? "bg-brand text-white hover:bg-brand-hover" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700"}`}>
                  {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><CreditCard className="w-4 h-4" /> Buy</>)}
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-8 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-6 text-center" data-testid="pricing-note">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            <strong className="text-zinc-900 dark:text-white">What can you do with credits?</strong><br />
            1 credit = 1 social post, product description, or video script &middot; 2 credits = 1 blog post or email &middot; 5-10 credits = 1 AI video (TikTok/Reel/Short)
          </p>
        </div>
      </div>
    </AppShell>
  );
}
